import { Component } from '@theme/component';

/** @extends Component<{form: HTMLFormElement, submit: HTMLButtonElement, status: HTMLElement, review: HTMLAnchorElement}> */
class WarrantyRegistration extends Component {
  requiredRefs = ['form', 'submit', 'status', 'review'];
  #controller;
  #redirectTimer;
  #busy = false;
  #complete = false;
  #requestId = crypto.randomUUID();
  #comment = '';

  connectedCallback() { super.connectedCallback(); this.refs.submit.disabled = this.#busy || this.#complete; this.updateRating(); this.#restoreResult(); }
  /** Updates the action label after native keyboard or pointer rating selection. */
  updateRating() {
    const rating = Number(new FormData(this.refs.form).get('rating'));
    const valid = [...this.refs.form.elements].every((field) => !field.willValidate || (field.validity.valid && (!(field.required && ['text', 'textarea', 'email'].includes(field.type)) || field.value.trim().length > 0)));
    this.refs.submit.textContent = this.dataset.submitLabel;
    this.refs.submit.disabled = !valid || this.#busy || this.#complete;
    if (this.refs.reviewHint) this.refs.reviewHint.hidden = !valid || rating < 4 || this.#busy || this.#complete;
  }
  #restoreResult() {
    if (!new URL(location.href).searchParams.has('registration-result')) return;
    try {
      const raw = sessionStorage.getItem('biowind-warranty-receipt');
      if (!raw) return;
      const receipt = JSON.parse(raw);
      if (Date.now() - receipt.time > 600000 || receipt.path !== location.pathname) return;
      this.#showResult(receipt.test);
    } catch { /* Keep the form available when session storage is unavailable. */ }
  }
  #showResult(test) {
    this.refs.form.hidden = true;
    this.refs.result.hidden = false;
    this.refs.resultMessage.textContent = test ? this.dataset.testSuccess : this.dataset.success;
    this.refs.resultMessage.focus();
  }
  /** Copies the submitted comment. Clipboard failure keeps the customer on the page. */
  async copyAndContinue() {
    if (!this.#complete) return;
    clearTimeout(this.#redirectTimer);
    try {
      if (this.#comment) await navigator.clipboard.writeText(this.#comment);
      if (!this.isConnected) return;
      this.refs.status.textContent = this.#comment ? this.dataset.copied : this.dataset.success;
      const destination = new URL(this.dataset.reviewUrl, location.origin);
      if (destination.protocol !== 'https:') return;
      this.refs.copy.hidden = true;
      this.#redirectTimer = setTimeout(() => { if (this.isConnected) location.assign(destination.href); }, 1500);
    } catch {
      if (!this.isConnected) return;
      this.refs.status.textContent = this.dataset.copyFailed;
      this.refs.copy.hidden = false;
      const comment = this.refs.form.querySelector('textarea[name="comment"]');
      comment?.focus();
      comment?.select();
    }
  }
  updatedCallback() {
    super.updatedCallback();
    this.#cancel();
    this.updateRating();
  }
  disconnectedCallback() { this.#cancel(); super.disconnectedCallback(); }
  #cancel() {
    this.#controller?.abort();
    this.#controller = undefined;
    clearTimeout(this.#redirectTimer);
    this.#busy = false;
  }

  /** @param {SubmitEvent} event - The native registration submission. */
  async submitRegistration(event) {
    event.preventDefault();
    if (this.#busy || this.#complete || !this.refs.form.reportValidity()) return;
    const form = this.refs.form;
    const values = new FormData(form);
    const orderNumber = String(values.get('orderNumber') || '').trim();
    const rating = Number(values.get('rating'));
    if (!orderNumber || !values.has('extendedWarranty') || !Number.isInteger(rating) || rating < 1 || rating > 5) return;
    this.#busy = true;
    this.refs.submit.disabled = true;
    if (this.refs.reviewHint) this.refs.reviewHint.hidden = true;
    form.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = '';
    this.#controller = new AbortController();
    const controller = this.#controller;
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const payload = {
        requestId: this.#requestId,
        name: String(values.get('name') || '').trim(),
        email: String(values.get('email') || '').trim(),
        orderNumber, warrantyMonths: 30, rating,
        comment: String(values.get('comment') || '').trim(),
      };
      if (!this.isConnected || controller.signal.aborted) throw new Error('Request cancelled');
      let result;
      if (this.dataset.testMode === 'true') {
        const records = JSON.parse(sessionStorage.getItem('biowind-warranty-tests') || '[]');
        records.push({ ...payload, createdAt: new Date().toISOString() });
        sessionStorage.setItem('biowind-warranty-tests', JSON.stringify(records.slice(-20)));
        result = { ok: true, registrationId: `test-${this.#requestId}` };
      } else {
      const endpoint = new URL(this.dataset.endpoint, location.origin);
      if (endpoint.protocol !== 'https:') throw new Error('HTTPS required');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Registration rejected');
      result = await response.json();
      }
      if (result.ok !== true || typeof result.registrationId !== 'string' || !result.registrationId.trim()) throw new Error('Save not confirmed');
      if (!this.isConnected || this.#controller !== controller) return;
      this.#complete = true;
      this.refs.status.textContent = this.dataset.testMode === 'true' ? this.dataset.testSuccess : this.dataset.success;
      this.refs.status.focus();
      if (rating >= 4 && this.dataset.reviewUrl) {
        const destination = new URL(this.dataset.reviewUrl, location.origin);
        if (destination.protocol === 'https:') {
          this.refs.review.href = destination.href;
          this.refs.review.hidden = false;
          this.#comment = payload.comment;
          this.refs.copy.hidden = false;
          await this.copyAndContinue();
        }
      } else {
        const test = this.dataset.testMode === 'true';
        try {
          sessionStorage.setItem('biowind-warranty-receipt', JSON.stringify({ time: Date.now(), path: location.pathname, test }));
          const destination = new URL(location.href);
          destination.searchParams.set('registration-result', 'submitted');
          location.assign(destination.href);
        } catch { this.#showResult(test); }
      }
    } catch {
      if (this.isConnected && this.#controller === controller) {
        this.refs.status.textContent = this.dataset.error;
        this.refs.status.focus();
      }
    } finally {
      clearTimeout(timeout);
      if (this.#controller === controller) {
        this.#busy = false;
        form.removeAttribute('aria-busy');
        if (this.isConnected) this.refs.submit.disabled = this.#complete;
      }
    }
  }
}
if (!customElements.get('warranty-registration')) customElements.define('warranty-registration', WarrantyRegistration);
